import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { Icon } from '@unite-us/app-components';
import DialogV2 from 'common/modal/DialogV2';
import useDeleteNote from 'src/actions/Case/Contact/Group/useDeleteNote';
import IconButton from 'common/buttons/IconButton/IconButton';
import { useInvalidateQueries } from 'src/api/APIHooks';
import { isHttpSuccess } from 'src/api/utils/httpStatus';
import Notifier from 'common/helpers/Notifier';
import NoteDeleteContent from './NoteDeleteContent';
import EditNoteForm from './EditNoteForm';
import { EDITABLE_NOTE_CATEGORIES, NOTE_VISIBILITY } from './constants';
import './Note.scss';

const SideDate = ({ day, month, year }) => (
  <div className="inline-block flex-none w-24 align-middle text-center">
    <p className="pb-1">{day ? day.toString() : null}</p>
    <p className="pb-1">{month}</p>
    <p>{year}</p>
  </div>
);

SideDate.propTypes = {
  day: PropTypes.string,
  month: PropTypes.string,
  year: PropTypes.string,
};

SideDate.defaultProps = {
  day: null,
  month: null,
  year: null,
};

const Message = (props) => {
  const {
    interactionDate,
    person,
    sender,
    text,
  } = props;
  const day = get(interactionDate, 'day', '');
  const month = get(interactionDate, 'month', '');
  const year = get(interactionDate, 'year', '');
  const time = get(interactionDate, 'time', '');
  /* eslint-disable react/no-danger */
  return (
    <div className="flex p-3 py-4 bg-white border border-solid border-medium-border-blue rounded">
      <SideDate day={day} month={month} year={year} />
      <div className="inline-block flex-1 w-64 align-middle">
        <b>Message</b> sent to {person.full_name} by {sender.full_name} on
        {` ${month} ${day}, ${year}`} at {time}
        <p className="mt-2 whitespace-pre-wrap" dangerouslySetInnerHTML={{ __html: text }} />
      </div>
    </div>
  );
  /* eslint-enable react/no-danger */
};

Message.propTypes = {
  interactionDate: PropTypes.object.isRequired,
  person: PropTypes.object.isRequired,
  sender: PropTypes.object.isRequired,
  text: PropTypes.string.isRequired,
};

const Note = (props) => {
  const {
    currentEmployeeId,
    interactionDate,
    interactionType,
    text,
    duration,
    note,
    noteType,
    category,
    details,
    reason,
    visibility,
    showDeleteNoteIcon,
    showEditNoteIcon,
    isEditing,
    onEdit,
    onCancelEdit,
    renderTextAsHtml,
  } = props;

  const {
    _processedNoteType: type,
    _processedAdded: added,
    _processedDate: date,
  } = note;

  const showInteractionType = interactionType !== '';
  const showDate = category !== 'general';
  const serviceProvided = get(details, 'service_provided', null);
  const serviceProvidedType = get(serviceProvided, 'type', '');
  const serviceProvidedAmount = get(serviceProvided, 'amount', '');
  const unit = get(serviceProvided, 'unit', '');
  const serviceProvidedUnit =
    unit === 'dollar' && serviceProvidedAmount > 1 ? `${unit}s` : unit;
  const showReason = reason && get(details, 'context', null);
  const deletable = get(details, 'deletable', true);
  const showDeleteButton =
    showDeleteNoteIcon && currentEmployeeId === get(note, 'employee.id');
  const canDeleteNote = showDeleteButton && deletable;
  const showEditButton =
    showEditNoteIcon &&
    currentEmployeeId === get(note, 'employee.id') &&
    EDITABLE_NOTE_CATEGORIES.includes(category);
  const deleteNoteDialogRef = useRef(null);
  const invalidateQueries = useInvalidateQueries();
  const { deleteRecord: deleteNote } = useDeleteNote();

  if (noteType === 'Message') {
    return <Message {...props} />;
  }

  if (isEditing) {
    return (
      <div
        className="employee_note_item employee_note_item--editing"
      >
        <EditNoteForm note={note} onCancel={onCancelEdit} />
      </div>
    );
  }

  const day = get(interactionDate, 'day', '');
  const month = get(interactionDate, 'month', '');
  const year = get(interactionDate, 'year', '');
  const className = showDeleteButton ? 'employee_note_item' : 'note_item';

  const deleteButtonOnClickHandler = (e) => {
    e.stopPropagation();
    deleteNoteDialogRef.current.openDialog();
  };
  const confirmDelete = async () => {
    deleteNoteDialogRef.current.closeDialog();
    const response = await deleteNote(get(note, 'id'), {});
    if (isHttpSuccess(response.status)) {
      Notifier.dispatch(response.status, 'Note deleted');
      invalidateQueries('notes');
    }
  };
  const cancelDelete = () => {
    deleteNoteDialogRef.current.closeDialog();
  };

  return (
    <div
      className={`flex ${className} p-3 py-4 bg-white border border-solid border-medium-border-blue rounded`}
    >
      <SideDate day={day} month={month} year={year} />
      <div className="inline-block flex-1 w-64 align-middle">
        {type && date ? (
          <p className="pb-2">
            <strong>{type}</strong>
            {added} by <strong>{ get(note, 'employee.full_name', '') }</strong> on {date}
          </p>
        ) : null}
        {showInteractionType && (
          <p className="pb-2">Interaction Type: {interactionType}</p>
        )}
        {showDate ? (
          <p className="pb-2">
            Date:{' '}
            {get(
              details,
              category === 'service_provided' ?
                'service_provided.date' :
                'interaction.date',
              '',
            )}
          </p>
        ) : null}

        {duration ? <p className="pb-2">Duration: {duration}</p> : null}
        {serviceProvided ? (
          <div>
            <p className="pb-2">Service: {serviceProvidedType}</p>
            <p className="pb-2">
              Amount: {`${serviceProvidedAmount} ${serviceProvidedUnit}`}
            </p>
          </div>
        ) : null}
        {showReason ? <p className="pb-2">Reason: {reason} </p> : null}
        <strong className="font-bold pb-2">{noteType}:</strong>
        {/* eslint-disable react/no-danger */}
        <p className="whitespace-pre-wrap">
          {
            renderTextAsHtml ? (
              <p dangerouslySetInnerHTML={{ __html: text }} />
            ) : text
          }
        </p>
        {/* eslint-disable react/no-danger */}
      </div>
      {visibility === NOTE_VISIBILITY.PRIVATE && (
        <div className="inline-block flex-1 w-64 align-middle text-right text-medium-gray">
          <Icon icon="EyeDisabled" /> Private to my organization
        </div>
      )}
      <div className="flex-col self-center">
        {showEditButton && (
          <IconButton
            className={`${className}_edit-btn mb-2`}
            labelText="Edit Note"
            icon="Pencil"
            onClick={onEdit}
          />
        )}
        {canDeleteNote && (
          <IconButton
            className={`${className}_delete-btn`}
            labelText="Delete Note"
            icon="Delete"
            onClick={(e) => deleteButtonOnClickHandler(e)}
          />
        )}
      </div>
      <DialogV2
        ref={deleteNoteDialogRef}
        title="Delete Note"
        confirmLabel="Delete"
        cancelHandler={cancelDelete}
        confirmationHandler={confirmDelete}
      >
        <NoteDeleteContent />
      </DialogV2>
    </div>
  );
};

Note.defaultProps = {
  currentEmployeeId: '',
  duration: null,
  noteType: 'Note',
  note: {},
  details: {},
  reason: '',
  showDeleteNoteIcon: false,
  showEditNoteIcon: false,
  isEditing: false,
  onEdit: null,
  onCancelEdit: null,
  visibility: '',
  renderTextAsHtml: false,
};

Note.propTypes = {
  currentEmployeeId: PropTypes.string,
  interactionDate: PropTypes.object.isRequired,
  interactionType: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired,
  duration: PropTypes.string,
  category: PropTypes.string.isRequired,
  details: PropTypes.object,
  reason: PropTypes.string,
  note: PropTypes.object,
  noteType: PropTypes.string,
  visibility: PropTypes.string,
  showDeleteNoteIcon: PropTypes.bool,
  showEditNoteIcon: PropTypes.bool,
  isEditing: PropTypes.bool,
  onEdit: PropTypes.func,
  onCancelEdit: PropTypes.func,
  renderTextAsHtml: PropTypes.bool,
};

export default Note;
